import {
  Alert,
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  Link,
  Menu,
  Portal,
  Table,
  Text,
} from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router'

import {
  Checkbox,
  LoadingState as SharedLoadingState,
} from '@mangovoice/mango-ui'

import { useDevicesI18n } from '../i18n'
import type { ProvisioningDeviceListItem } from '../types'
import { formatMac } from '../utils'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const dateTimeFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

const dateOnlyFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
})

function formatDate(
  dateStr: string | null | undefined,
  formatter: Intl.DateTimeFormat,
): string {
  if (!dateStr) return '—'
  try {
    return formatter.format(new Date(dateStr))
  } catch {
    return '—'
  }
}

type WarrantyStatus = 'unknown' | 'in_warranty' | 'expired'

function getWarrantyStatus(
  warrantyExpiresAt: string | null | undefined,
): WarrantyStatus {
  if (!warrantyExpiresAt) return 'unknown'
  try {
    const expires = new Date(warrantyExpiresAt)
    return expires > new Date() ? 'in_warranty' : 'expired'
  } catch {
    return 'unknown'
  }
}

// ---------------------------------------------------------------------------
// Small presentational components
// ---------------------------------------------------------------------------

export const LoadingState = () => {
  const { text } = useDevicesI18n()
  return (
    <Box
      height="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="bg.subtle"
      borderRadius="lg"
    >
      <SharedLoadingState
        title={text('devices.row.loadingDevices', 'Loading devices...')}
      />
    </Box>
  )
}

export const ErrorState = ({ message }: { message: string }) => {
  const { text } = useDevicesI18n()
  return (
    <Alert.Root status="error" p={4}>
      <Alert.Indicator />
      <Box>
        <Text fontWeight="bold">{text('devices.common.error', 'Error')}</Text>
        <Text>{message}</Text>
      </Box>
    </Alert.Root>
  )
}

export const EmptyState = () => {
  const { text } = useDevicesI18n()
  return (
    <Alert.Root status="info" p={4}>
      <Alert.Indicator />
      <Box>
        <Text fontWeight="bold">
          {text('devices.row.noDevices', 'No devices')}
        </Text>
        <Text>
          {text(
            'devices.row.noDevicesDescription',
            'There are no devices for this PBX yet.',
          )}
        </Text>
      </Box>
    </Alert.Root>
  )
}

// ---------------------------------------------------------------------------
// DeviceRow
// ---------------------------------------------------------------------------

export const DeviceRow = ({
  device,
  actionsEnabled,
  isStaff,
  isSelected,
  onToggleSelect,
  onDeleteClick,
  onCloneClick,
  getEditRoute,
  truncate,
}: {
  device: ProvisioningDeviceListItem
  actionsEnabled: boolean
  isStaff: boolean
  isSelected: boolean
  onToggleSelect: (device: ProvisioningDeviceListItem) => void
  onDeleteClick: (device: ProvisioningDeviceListItem) => void
  onCloneClick: (device: ProvisioningDeviceListItem) => void
  getEditRoute: (deviceId: number) => string
  truncate?: boolean
}) => {
  const { text } = useDevicesI18n()
  const editUrl = getEditRoute(device.id)
  const stationLabel = device.station_label || '—'
  const modelName =
    device.device_model_name ??
    text('devices.row.modelFallback', 'Model {{id}}', {
      id: device.device_model,
    })
  const mac = device.device_mac_address
    ? formatMac(device.device_mac_address)
    : '—'
  const user = device.assigned_user?.full_name ?? '—'
  const ext = device.primary_extension?.number ?? '—'
  const lastProvisioned = formatDate(
    device.last_provisioned_time,
    dateTimeFormatter,
  )
  const lastProvisionedFrom = device.last_provisioned_from
  const createdAt = formatDate(device.created_at, dateOnlyFormatter)
  const warrantyExpiresAt = formatDate(
    device.warranty_expires_at,
    dateOnlyFormatter,
  )
  const warrantyStatus = getWarrantyStatus(device.warranty_expires_at)
  const rowActionsAllowed =
    actionsEnabled && (device.pbx_active === undefined || device.pbx_active)
  // Backend stores empty strings rather than null when no firmware has been
  // self-reported or set, so treat empty/whitespace as missing (matches Admin2
  // FirmwareColumn). When self-reported is missing we show "unknown" but still
  // surface the desired firmware suffix when one is configured, so admins can
  // see what the device should upgrade to even before its first check-in.
  const selfReportedFirmware =
    device.self_reported_firmware_version?.trim() ?? ''
  const desiredFirmware = device.desired_firmware_version?.trim() ?? ''
  const baseFirmwareLabel = selfReportedFirmware
    ? selfReportedFirmware
    : text('devices.row.firmwareUnknown', 'unknown')
  const showDesiredSuffix =
    desiredFirmware !== '' && desiredFirmware !== selfReportedFirmware
  const firmwareDisplay = showDesiredSuffix
    ? `${baseFirmwareLabel} ${text('devices.row.desiredFirmware', '(desired: {{version}})', { version: desiredFirmware })}`
    : baseFirmwareLabel

  const showChangesPending =
    Boolean(device.has_new_config) && device.allow_provision_retrieval === true
  const modelMismatch =
    device.self_reported_model != null &&
    device.self_reported_model.trim() !== '' &&
    device.self_reported_model !== modelName

  const warrantyLabel =
    warrantyStatus === 'in_warranty'
      ? text('devices.row.warrantyInWarranty', 'In warranty')
      : warrantyStatus === 'expired'
        ? text('devices.row.warrantyExpired', 'Expired')
        : text('devices.row.warrantyUnknown', 'No warranty')
  const warrantyColorPalette: 'success' | 'error' | 'neutral' =
    warrantyStatus === 'in_warranty'
      ? 'success'
      : warrantyStatus === 'expired'
        ? 'error'
        : 'neutral'

  const overflowProps = truncate ? { overflow: 'hidden' as const } : {}

  return (
    <Table.Row>
      {actionsEnabled ? (
        <Table.Cell px={4} py={3} width="40px">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onToggleSelect(device)}
            aria-label={text('devices.row.selectDevice', 'Select {{name}}', {
              name: stationLabel,
            })}
          />
        </Table.Cell>
      ) : null}
      <Table.Cell px={4} py={3} {...overflowProps}>
        {actionsEnabled ? (
          <Link asChild>
            <RouterLink
              to={editUrl}
              aria-label={text('devices.row.editDevice', 'Edit {{name}}', {
                name: stationLabel,
              })}
            >
              <Text
                fontWeight="medium"
                color="colorPalette.fg"
                colorPalette="info"
                _hover={{ textDecoration: 'underline' }}
                truncate={truncate || undefined}
              >
                {stationLabel}
              </Text>
            </RouterLink>
          </Link>
        ) : (
          <Text truncate={truncate || undefined}>{stationLabel}</Text>
        )}
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Text truncate={truncate || undefined}>{ext}</Text>
      </Table.Cell>
      <Table.Cell px={4} py={3} {...overflowProps}>
        <Text truncate={truncate || undefined}>{user}</Text>
      </Table.Cell>
      <Table.Cell px={4} py={3} {...overflowProps}>
        <Flex
          direction="column"
          gap={1}
          alignItems="flex-start"
          {...(truncate ? { minW: 0 } : {})}
        >
          <Text truncate={truncate || undefined}>{modelName}</Text>
          {modelMismatch && (
            <Badge colorPalette="warning" size="sm" variant="subtle">
              {text('devices.row.reportsModel', 'Reports: {{model}}', {
                model: device.self_reported_model ?? '',
              })}
            </Badge>
          )}
        </Flex>
      </Table.Cell>
      <Table.Cell px={4} py={3} {...overflowProps}>
        <Text fontFamily="mono" fontSize="sm" truncate={truncate || undefined}>
          {mac}
        </Text>
      </Table.Cell>
      <Table.Cell px={4} py={3} {...overflowProps}>
        <Text truncate={truncate || undefined}>{firmwareDisplay}</Text>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Flex direction="column" gap={1} alignItems="flex-start">
          <Text>{warrantyExpiresAt}</Text>
          <Badge colorPalette={warrantyColorPalette} px={2} py={0.5} size="sm">
            {warrantyLabel}
          </Badge>
        </Flex>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Flex direction="column" gap={0.5} alignItems="flex-start">
          <Text>{lastProvisioned}</Text>
          {lastProvisionedFrom && (
            <Text fontSize="xs" color="fg.muted">
              {text('devices.row.fromSource', 'from {{source}}', {
                source: lastProvisionedFrom,
              })}
            </Text>
          )}
        </Flex>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Text>{createdAt}</Text>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Flex gap={1} flexWrap="wrap">
          {showChangesPending && (
            <Badge colorPalette="warning" size="sm" variant="subtle">
              {text('devices.row.changesPending', 'Changes Pending')}
            </Badge>
          )}
          <Badge colorPalette="neutral" size="sm" variant="subtle">
            {device.transport ?? 'UDP'}
          </Badge>
          {device.sidecar_enabled && (
            <Badge colorPalette="accent" size="sm" variant="subtle">
              {text('devices.row.sidecar', 'Sidecar')}
            </Badge>
          )}
          {device.allow_provision_retrieval === false && (
            <Badge colorPalette="warning" size="sm" variant="subtle">
              {text('devices.row.noProv', 'No Prov')}
            </Badge>
          )}
          {isStaff && device.source && (
            <Badge colorPalette="info" size="sm" variant="subtle">
              {device.source}
            </Badge>
          )}
        </Flex>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        {rowActionsAllowed ? (
          <Menu.Root>
            <Menu.Trigger asChild>
              <Button
                size="sm"
                variant="outline"
                px={2}
                aria-label={text('devices.row.rowActions', 'Row actions')}
              >
                <Icon boxSize={4}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="12" cy="5" r="2" />
                    <circle cx="12" cy="12" r="2" />
                    <circle cx="12" cy="19" r="2" />
                  </svg>
                </Icon>
              </Button>
            </Menu.Trigger>
            <Portal>
              <Menu.Positioner>
                <Menu.Content>
                  <Menu.Item
                    value="clone"
                    onClick={() => onCloneClick(device)}
                    px={4}
                    py={2}
                  >
                    {text('devices.row.cloneAction', 'Clone')}
                  </Menu.Item>
                  {isStaff && (
                    <Menu.Item
                      value="delete"
                      onClick={() => onDeleteClick(device)}
                      px={4}
                      py={2}
                      color="fg.error"
                    >
                      {text('devices.row.deleteAction', 'Delete')}
                    </Menu.Item>
                  )}
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
        ) : (
          '—'
        )}
      </Table.Cell>
    </Table.Row>
  )
}
